import React, { useEffect, useState } from "react";
import { MenuItem } from "../../@types/stores/menu";
import menuStore from "../../stores/menu";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {getAllergenById, getCurrency, getHost} from "../../helpers/ApiHelper";
import { ReactComponent as IconX } from "../../assets/icons/x.svg";
import { useNavigate } from "react-router";
import { moveToTop } from "../../helpers/DomHelper";
import { ArticleViewType } from "../../@types/enums/ArticleViewType";
import orderStore from "../../stores/order";
import { v4 as guid } from 'uuid';
import defaultArticleImage from "../../assets/images/default.webp";
import "./style.scss";
import { hideLoader, showLoader } from "../../helpers/ComponentHelper";
import {OrderLine} from "../../@types/stores/order";
import useLayoutStyles from "../../hooks/styles";

export interface ArticleInfoBlockProps {
    isRecommendationPage?: boolean;
}

const ArticleInfoBlock = ({isRecommendationPage}: ArticleInfoBlockProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const styles = useLayoutStyles();

    const [item, setItem] = useState<MenuItem | undefined>(undefined);

    useEffect(() => {
        setItem(menuStore.navigation.recommendationGroupId ? menuStore.getRecommendationItem() : menuStore.getItem());
    }, [menuStore.navigation.item, menuStore.navigation.recommendationItem, menuStore.navigation.recommendationGroupId]);

    if (!item)
        return null;

    const close = () => {
        if (isRecommendationPage)
            menuStore.changeRecommendationItem();
        else
            menuStore.changeMenuItem();
    }

    const goNext = async () => {
        if (menuStore.navigation.recommendationGroupId)
            await goToRecommendationItem();
        else
            await goToMenuItem();
    }

    const goToRecommendationItem = async () => {
        moveToTop();

        // ONE_PAGE
        if (!localStorage.getItem("articleViewType") || localStorage.getItem("articleViewType") === ArticleViewType.ONE_PAGE) {
            navigate(`/recommendations/${menuStore.navigation.recommendationGroupId}/${item.number}`);
        }

        // GROUP_PAGE
        else if (localStorage.getItem("articleViewType") === ArticleViewType.GROUP_PAGE) {
            if (item.modifierGroups?.length) {
                navigate(`/recommendations/${menuStore.navigation.recommendationGroupId}/${item.number}/modifiers`);
                return;
            }
            else if (item.addonGroups?.length) {
                navigate(`/recommendations/${menuStore.navigation.recommendationGroupId}/${item.number}/addons`);
                return;
            }
            else if (item.ingredientGroups?.length) {
                navigate(`/recommendations/${menuStore.navigation.recommendationGroupId}/${item.number}/ingredients`);
                return;
            }
        }

        // INDIVIDUAL_PAGE
        else if (localStorage.getItem("articleViewType") === ArticleViewType.INDIVIDUAL_PAGE) {
            if (item.modifierGroups?.length) {
                navigate(`/recommendations/${menuStore.navigation.recommendationGroupId}/${item.number}/modifiers/${item.modifierGroups.at(0)?.id}`);
                return;
            }
            else if (item.addonGroups?.length) {
                navigate(`/recommendations/${menuStore.navigation.recommendationGroupId}/${item.number}/addons/${item.addonGroups.at(0)?.id}`);
                return;
            }
            else if (item.ingredientGroups?.length) {
                navigate(`/recommendations/${menuStore.navigation.recommendationGroupId}/${item.number}/ingredients`);
                return;
            }
        }

        let navigateTo = "/menu";
        if (menuStore.navigation.item) {
            const item = menuStore.getItemById(menuStore.navigation.item);

            if (item?.recommendedGroups?.length) {
                const nextGroupIdIndex = item.recommendedGroups.findIndex(x => x === menuStore.navigation.recommendationGroupId) + 1;
                if (nextGroupIdIndex && nextGroupIdIndex < item.recommendedGroups.length) {
                    const nextGroupId = item.recommendedGroups.at(nextGroupIdIndex);
                    navigateTo = `/recommendations/${nextGroupId}`;
                }
            }
        }

        await addToOrder(item, navigateTo);
        return;
    }

    const goToMenuItem = async () => {
        moveToTop();

        // ONE_PAGE
        if (!localStorage.getItem("articleViewType") || localStorage.getItem("articleViewType") === ArticleViewType.ONE_PAGE) {
            moveToTop();
            navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}`);
        }

        // GROUP_PAGE
        else if (localStorage.getItem("articleViewType") === ArticleViewType.GROUP_PAGE) {
            if (item.modifierGroups?.length) {
                navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/modifiers`);
            }
            else if (item.addonGroups?.length) {
                navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/addons`);
            }
            else if (item.ingredientGroups?.length) {
                moveToTop();
                navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/ingredients`);
                return;
            }
        }

        // INDIVIDUAL_PAGE
        else if (localStorage.getItem("articleViewType") === ArticleViewType.INDIVIDUAL_PAGE) {
            if (item.modifierGroups?.length) {
                navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/modifiers/${item.modifierGroups.at(0)?.id}`);
            }
            else if (item.addonGroups?.length) {
                navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/addons/${item.addonGroups.at(0)?.id}`);
            }
            else if (item.ingredientGroups?.length) {
                moveToTop();
                navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/ingredients`);
                return;
            }
        }

        await addToOrder(item, "/recommendations");
        return;
    }

    const addToOrder = async (item: MenuItem, navigateTo: string) => {
        showLoader();

        const currentOrderLine: OrderLine = {
            id: guid(),
            menuId: menuStore.navigation.category,
            subMenuId: menuStore.navigation.subCategory,
            recommendationGroupId: menuStore.navigation.recommendationGroupId,
            name: item.name,
            number: item.number,
            count: 1,
            price: item.price,
            total: item.price,
            img: item.previewList
        };

        orderStore.setCurrentOrderLine = currentOrderLine;

        const r = await orderStore.addToOrder(currentOrderLine);
        if (r) {
            navigate(navigateTo);
        }
        hideLoader();
    }

    return <div className="ArticleInfoBlockBG">
        <div className="ArticleInfoBlock">
            <div className="img" style={{backgroundImage: `url(${item.previewEdit ? `${getHost()}${item.previewEdit}` : (styles?.article.defaultEditImage ? `${getHost()}${styles.article.defaultEditImage}` : defaultArticleImage)})`}} >
                <div className="close" onClick={close}><IconX /></div>
            </div>
            <div className="text">
                <div className="title">
                    {item.name}
                </div>
                {item.description}
                <div className="allergens">
                    {item.allergens?.map(allergenId => getAllergenById(allergenId)?.name || null).filter(name => !!name).map(name => <div key={name} className="allergen">{name}</div>)}
                </div>
            </div>
            <div className="footer">
                <div className="price">
                    {item.price.toFixed(2)} {getCurrency()}
                </div>
                <div className="button dark" onClick={goNext}>{item?.modifierGroups?.length || item?.addonGroups?.length || item?.ingredientGroups?.length ? t("select") : t("addToOrder")}</div>
            </div>
        </div>
    </div>
}

export default observer(ArticleInfoBlock);
